import { toKeys } from "@suse-o11y/to-keys";
import type { Location, LocationDescriptorObject } from "history";
import isString from "lodash/isString";
import pick from "lodash/pick";
import qs from "query-string";
import {
	BooleanParam,
	type DecodedValueMap,
	type EncodedQuery,
	NumberParam,
	type QueryParamConfig,
	type QueryParamConfigMap,
	StringParam,
	decodeEnum,
	decodeQueryParams,
	encodeQueryParams,
	encodeString,
} from "serialize-query-params";

import { ApplicationDomain } from "~/models/dto";

import { analyticsParamsMap } from "../../../bottom_content/analytics/AnalyticsUtils";
import { viewFilterParam } from "../../../bottom_content/overview/overview-filter";
import { tracesExplorerSearchParams } from "../../../components/traces-explorer/traces-explorer-search-params";
import { QueryParam as MetricsQueryParam } from "../../../metrics/query-param";
import { NotificationsState } from "../../../notifications/NotificationsState";
import { dashboardFilterParam } from "../../../routes/dashboards/dashboard-filter";
import { dashboardVariablesParam } from "../../../routes/dashboards/dashboard-variables-param";
import { timelineParamsMap } from "../../../routes/timeline/TimelineQueryUtils";
import { RightPaneState } from "../right-pane/RightPaneState";
import { viewParamsMap } from "../view/viewpoint/ViewPointQueryUtils";

const DomainParam: QueryParamConfig<undefined | ApplicationDomain> = {
	encode: encodeString,
	decode: (input): undefined | ApplicationDomain =>
		(decodeEnum(input, toKeys(ApplicationDomain)) as ApplicationDomain) ?? undefined,
};

const ALL_SEARCH_PARAMS = {
	...timelineParamsMap,
	...viewParamsMap,
	...analyticsParamsMap,
	...RightPaneState.searchParams,
	...NotificationsState.searchParams,
	...tracesExplorerSearchParams,
	metrics: MetricsQueryParam,
	metricTab: StringParam, // name of an active sub tab with metrics (check MetricBinding layout)
	viewFilter: viewFilterParam,
	dashboardFilter: dashboardFilterParam,
	dashboardVariables: dashboardVariablesParam,
	type: StringParam,
	domain: DomainParam,
	componentId: NumberParam as QueryParamConfig<number | undefined>,
	loggedOut: BooleanParam,
};

type AllSearchParam = typeof ALL_SEARCH_PARAMS;

/**
 * Maps each param config to the type its `encode` function accepts, i.e. the value supplied
 * when building a URL. This is the write/input side and may be wider than the decode output.
 */
type SearchParamsInputMap<QPCMap extends QueryParamConfigMap> = {
	[P in keyof QPCMap]?: Parameters<QPCMap[P]["encode"]>[0];
};

export type SearchParamsInput = SearchParamsInputMap<AllSearchParam>;

export type SearchParamsOutput = DecodedValueMap<AllSearchParam>;

export const STICKY_SEARCH_PARAMS: ReadonlyArray<keyof SearchParamsInput> = ["domain"];

export interface DecodeAllSearchParamsOptions {
	/**
	 * If true, only params present in the query string will be decoded.
	 * Otherwise, all params are decoded, and missing params have default decoding values.
	 *
	 * @default false
	 *
	 * @example
	 * decodeAllSearchParams({ search: "?query=anything" }, { decodeOnlyPresentParams: true })
	 * // => { query: "anything" }
	 *
	 * decodeAllSearchParams({ search: "?query=anything" })
	 * // => { query: "anything", rightPane: RightPaneState.defaults, ...etc }
	 */
	decodeOnlyPresentParams: true;
}

export function decodeAllSearchParams(
	locationOrSearch: string | Location | LocationDescriptorObject,
	options: DecodeAllSearchParamsOptions,
): Partial<SearchParamsOutput>;

export function decodeAllSearchParams(
	locationOrSearch: string | Location | LocationDescriptorObject,
): SearchParamsOutput;

export function decodeAllSearchParams(
	locationOrSearch: string | Location | LocationDescriptorObject,
	options?: DecodeAllSearchParamsOptions,
): Partial<SearchParamsOutput> {
	const parsedQuery = qs.parse(isString(locationOrSearch) ? locationOrSearch : (locationOrSearch.search ?? ""));
	const params = options?.decodeOnlyPresentParams ? pick(ALL_SEARCH_PARAMS, toKeys(parsedQuery)) : ALL_SEARCH_PARAMS;

	return decodeQueryParams(params, parsedQuery);
}

/**
 * The `encodeQueryParams` function from `serialize-query-params` types its `query` argument as
 * `Partial<DecodedValueMap>` (the decode output), assuming each param's encode input and decode
 * output coincide. When a config's `encode` accepts a wider input than its `decode` returns, that
 * signature is too narrow. This binding restores the accurate signature: `encode` consumes
 * `Parameters<encode>[0]`.
 */
const encodeQueryParamsWithInput: <QPCMap extends QueryParamConfigMap>(
	paramConfigMap: QPCMap,
	query: SearchParamsInputMap<QPCMap>,
) => EncodedQuery = encodeQueryParams;

export const encodeAllSearchParams = (params: undefined | SearchParamsInput): EncodedQuery =>
	encodeQueryParamsWithInput(ALL_SEARCH_PARAMS, params ?? {});
